import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Moon, Sun, LogOut, User, Trash2, Menu } from 'lucide-react';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';

const Navbar = () => {
  const { theme, toggleTheme } = useTheme();
  const { user, logout, deleteAccount, loginEnabled, registerEnabled } = useAuth();
  const { pathname } = useLocation();
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const nav = useRef(null);
  useEffect(() => {
    const observer = new ResizeObserver(([entry]) => document.documentElement.style.setProperty('--nav-height', `${entry.target.getBoundingClientRect().height}px`));
    observer.observe(nav.current);
    return () => { observer.disconnect(); document.documentElement.style.removeProperty('--nav-height'); };
  }, []);
  const links = [
    ['Gallery', '/', 'gallery'], ['About', '/about', 'about'], ['Contact', '/contact', 'contact'],
    ...(user?.is_admin ? [['Admin panel', '/admin', 'manage']] : []),
    ...(!user && loginEnabled ? [['Login', '/login', 'login']] : []),
    ...(!user && registerEnabled ? [['Register', '/register', 'register']] : []),
  ];
  const currentPage = links.find(([, path]) => path === pathname)?.[0]
    || ({ '/privacy': 'Privacy', '/privacy-policy': 'Privacy', '/forgot-password': 'Password recovery', '/reset-password': 'Reset password', '/verify-email': 'Email verification' })[pathname]
    || (pathname.startsWith('/photos/') ? 'Photo' : pathname.startsWith('/collections/') ? 'Collection' : 'Menu');
  const handleDeleteAccount = async () => {
    setDeleting(true);
    try { await deleteAccount(); }
    catch { toast.error('Could not delete your account. Please try again.'); }
    finally { setDeleting(false); setConfirmDelete(false); }
  };
  const accountItems = user && <>
    <div className="px-3 py-2 min-w-0">
      <p className="text-sm font-medium break-words">{user.name}</p>
      <p className="text-xs text-muted-foreground break-words">{user.email}</p>
    </div>
    <DropdownMenuSeparator />
    <DropdownMenuItem onSelect={logout} className="min-h-11 rounded-lg" data-testid="logout-btn"><LogOut className="w-4 h-4" />Logout</DropdownMenuItem>
    <DropdownMenuItem onSelect={() => setConfirmDelete(true)} disabled={deleting} className="min-h-11 rounded-lg text-destructive"><Trash2 className="w-4 h-4" />Delete Account</DropdownMenuItem>
  </>;

  return <>
    <nav ref={nav} aria-label="Main navigation" className={`top-0 left-0 right-0 z-50 nav-cinema ${pathname === '/' ? 'nav-cinema--home' : ''}`}>
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
        <div className="flex flex-wrap items-center justify-between min-h-16 md:min-h-20 gap-x-3 gap-y-1 py-2">
          <Link to="/" className="nav-brand min-w-0 nav-text hover:opacity-75" data-testid="logo-link">
            <span className="text-lg md:text-2xl tracking-tight block" style={{ fontFamily: "'Cormorant Garamond', serif" }}>FERDIA'S GALLERY</span>
            <span className="lg:hidden block text-[0.65rem] tracking-wider">{currentPage}</span>
          </Link>
          <div className="hidden lg:flex items-center gap-5">
            {links.map(([label, path, id]) => <Link key={path} to={path} aria-current={pathname === path ? 'page' : undefined} className={`nav-link nav-text text-xs uppercase tracking-wider ${pathname === path ? 'active nav-text-active' : ''}`} data-testid={`nav-${id}`}>{label}</Link>)}
            {user && <DropdownMenu>
              <DropdownMenuTrigger asChild><button className="icon-btn nav-text" aria-label="Account menu" data-testid="user-menu"><User className="h-5 w-5" /></button></DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-64 rounded-xl">{accountItems}</DropdownMenuContent>
            </DropdownMenu>}
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={toggleTheme} className="icon-btn nav-text bg-black/15" data-testid="theme-toggle" aria-label={theme === 'dark' ? 'Use light theme' : 'Use dark theme'}>
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </button>
            <div className="lg:hidden">
              <DropdownMenu>
                <DropdownMenuTrigger asChild><button className="icon-btn nav-text bg-black/15" aria-label={`Open navigation, current page: ${currentPage}`} data-testid="mobile-menu-trigger"><Menu className="h-5 w-5" /></button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="mobile-navigation w-72 max-w-[calc(100vw-2rem)] rounded-2xl p-2" aria-label="Site navigation">
                  {links.map(([label, path, id]) => <DropdownMenuItem asChild key={path} className="min-h-11 rounded-lg px-3">
                    <Link to={path} aria-current={pathname === path ? 'page' : undefined} data-testid={`mobile-nav-${id}`}>{label}{pathname === path && <span aria-hidden="true" className="ml-auto text-xs">●</span>}</Link>
                  </DropdownMenuItem>)}
                  {user && <><DropdownMenuSeparator />{accountItems}</>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </div>
    </nav>
    <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
      <AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Delete your account?</AlertDialogTitle><AlertDialogDescription>This permanently removes your account. This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleting}>Keep account</AlertDialogCancel>
          <AlertDialogAction onClick={handleDeleteAccount} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete account</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>;
};
export default Navbar;
